import { Component, OnInit, OnDestroy, EventEmitter } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AngularFirestore } from '@angular/fire/compat/firestore';

import { DeliveryService, DriverAccount, DriverSupportSettings } from 'src/app/services/delivery.service';
import { AuthService } from 'src/app/services/auth.service';
import { ConfigService } from 'src/app/shared/config';
import { NotificationService } from 'src/app/shared/notification.service';
import { CommonPopup } from 'src/app/admin-dashboard/common-popup/common-popup';
import { DialogDelete } from 'src/app/dialog-delete/dialog-delete.component';
import { ColumnSetting } from 'src/app/layout/table-standard-layout/table-standard-layout.component';
import { ActionButton, ActionButtonEvent } from 'src/app/layout/action-button-toolbar/action-button-toolbar.component';

// Driver accounts for the company the dashboard is showing — for super-admins
// and driver managers.
//
// A driver is a normal panel user (roles.driver = true) plus a driver profile
// with the extra data (phone, employer, role, panel features). Two lists:
//   * Drivers — profiles that belong to the selected company.
//   * New / unassigned — users ticked Driver in the role popup who belong to no
//     company yet; "Add" puts them on this company's list.
// A new driver is always created in the selected company. The name is the one
// on the user account (the same as in the admin user list), never a second copy.
//
// The driver is a separate identity from a customer portal login, and the
// `driver` custom claim is what the Firestore rules use to deny an account
// direct database access. The server refuses a portal account and refuses a
// non-super-admin touching an admin account — see driverAdminController.
interface DriverRow extends DriverAccount {
  roleLabel: string;
  panelAccess: string;
  statusLabel: string;
}

@Component({
  selector: 'app-delivery-drivers',
  templateUrl: './drivers.component.html',
  styleUrls: ['./drivers.component.css']
})
export class DeliveryDriversComponent implements OnInit, OnDestroy {

  // Super-admin or holder of the `driverManager` claim. Name kept for the template.
  _isSuperAdmin = false;
  loading = true;
  error = '';

  // Standard-table data sources.
  drivers$ = new BehaviorSubject<DriverRow[]>([]);
  unassigned$ = new BehaviorSubject<DriverRow[]>([]);
  unassignedCount = 0;
  selectedTab = 0;

  readonly roles = [
    { value: 'driver', label: 'Driver', hint: 'Runs the routes assigned to them' },
    { value: 'lead', label: 'Lead', hint: 'Runs routes and can adjust them in the field' }
  ];

  // Panel features a driver account can be given. The names are what the
  // Firestore/Storage rules check; the server drops anything not in its list.
  readonly featureOptions = [
    { value: 'delivery', label: 'Delivery', hint: 'Routes, dispatch, issues and location guides in this panel' }
  ];

  columnSettings: ColumnSetting[] = [
    { columnId: 'name', name: 'Name' },
    { columnId: 'email', name: 'Email', hideOnMobile: true },
    { columnId: 'phone', name: 'Phone', hideOnMobile: true },
    { columnId: 'employer', name: 'Employer', hideOnMobile: true },
    { columnId: 'roleLabel', name: 'Role', hideOnMobile: true },
    { columnId: 'panelAccess', name: 'Panel access', hideOnMobile: true },
    { columnId: 'statusLabel', name: 'Status' }
  ];
  unassignedColumns: ColumnSetting[] = [
    { columnId: 'name', name: 'Name' },
    { columnId: 'email', name: 'Email' }
  ];

  edit = new EventEmitter<ActionButtonEvent>();
  resend = new EventEmitter<ActionButtonEvent>();
  toggleStatus = new EventEmitter<ActionButtonEvent>();
  removeFromCompany = new EventEmitter<ActionButtonEvent>();
  addDriver = new EventEmitter<ActionButtonEvent>();
  addToCompany = new EventEmitter<ActionButtonEvent>();
  editUnassigned = new EventEmitter<ActionButtonEvent>();

  rowActionButtons: ActionButton[] = [
    { icon: 'edit', tooltip: 'Edit', eventEmitter: this.edit },
    { icon: 'mail', tooltip: 'Resend invite', eventEmitter: this.resend },
    { icon: 'person_remove', tooltip: 'Remove from this company', eventEmitter: this.removeFromCompany },
    { icon: 'block', tooltip: 'Deactivate / reactivate', eventEmitter: this.toggleStatus }
  ];
  unassignedActionButtons: ActionButton[] = [
    { icon: 'person_add', tooltip: 'Add to the selected company', eventEmitter: this.addToCompany },
    { icon: 'edit', tooltip: 'Edit details and add to the selected company', eventEmitter: this.editUnassigned }
  ];
  topbarActionButton: ActionButton[] = [
    { icon: 'person_add', tooltip: 'Add driver to this company', eventEmitter: this.addDriver }
  ];

  companyNameById: { [id: string]: string } = {};

  // Settings tab: the tap-to-call numbers in the driver app's menu. One set for
  // all drivers; they travel in the route bundle, so they work without signal.
  support: DriverSupportSettings = { supportPhone: '', roadAssistPhone: '', extraContacts: [] };
  supportSaving = false;
  supportLoaded = false;

  private allDrivers: DriverAccount[] = [];
  // Names from the panel user accounts, by uid: the single source of the name.
  private panelNames: { [uid: string]: string } = {};
  private subs: Subscription[] = [];

  constructor(
    private svc: DeliveryService,
    private auth: AuthService,
    private config: ConfigService,
    private notifier: NotificationService,
    private dialog: MatDialog,
    private afs: AngularFirestore
  ) { }

  ngOnInit() {
    this.subs.push(this.auth.user$.subscribe(user => {
      const isSuper = user ? this.auth.isSuperAdmin(user) : false;
      this.auth.isDriverManager().then(isManager => {
        this._isSuperAdmin = isSuper || isManager;
        if (this._isSuperAdmin) { this.load(); this.loadSupport(); }
        else this.loading = false;
      });
    }));

    this.subs.push(this.svc.companies$().subscribe(companies => {
      const map: { [id: string]: string } = {};
      companies.forEach(c => map[c.id] = c.name);
      this.companyNameById = map;
    }));

    // Panel user names, so a driver is listed under the name used in the admin
    // user list. One query for everyone ticked Driver.
    this.subs.push(this.afs.collection('users', ref => ref.where('roles.driver', '==', true))
      .snapshotChanges().subscribe(docs => {
        const names: { [uid: string]: string } = {};
        docs.forEach(d => { names[d.payload.doc.id] = (d.payload.doc.data() as any)?.Name || ''; });
        this.panelNames = names;
        this.render();
      }));

    this.subs.push(this.edit.subscribe(e => e.eventData && this.openDetails(e.eventData, false)));
    this.subs.push(this.editUnassigned.subscribe(e => e.eventData && this.openDetails(e.eventData, true)));
    this.subs.push(this.addDriver.subscribe(() => this.openAdd()));
    this.subs.push(this.addToCompany.subscribe(e => e.eventData && this.assign(e.eventData)));
    this.subs.push(this.resend.subscribe(e => e.eventData && this.resendInvite(e.eventData)));
    this.subs.push(this.toggleStatus.subscribe(e => e.eventData && this.setStatus(e.eventData)));
    this.subs.push(this.removeFromCompany.subscribe(e => e.eventData && this.removeFrom(e.eventData)));
  }

  ngOnDestroy() {
    this.subs.forEach(s => s.unsubscribe());
  }

  currentCompanyName() {
    const id = this.config.getCompanyId();
    return this.companyNameById[id] || id || 'the selected company';
  }

  private displayName(d: DriverAccount) {
    return this.panelNames[d.uid] || d.name || '';
  }

  private toRow(d: DriverAccount): DriverRow {
    const role = this.roles.find(r => r.value === d.role);
    const features = (d.features || []).map(f => this.featureOptions.find(o => o.value === f)?.label || f);
    return {
      ...d,
      name: this.displayName(d),
      roleLabel: role ? role.label : (d.role || ''),
      panelAccess: features.length ? features.join(', ') : '—',
      statusLabel: d.status ? 'Active' : 'Deactivated'
    };
  }

  // Split the profiles into "this company" and "unassigned" and push both to
  // the tables. Called after every load and when the panel names arrive.
  private render() {
    const companyId = this.config.getCompanyId();
    const unassigned = this.allDrivers.filter(d => d.profileMissing).map(d => this.toRow(d));
    const drivers = this.allDrivers
      .filter(d => !d.profileMissing && (!companyId || (d.companyIds || []).includes(companyId)))
      .map(d => this.toRow(d));
    this.unassignedCount = unassigned.length;
    this.unassigned$.next(unassigned);
    this.drivers$.next(drivers);
  }

  async load() {
    this.loading = true;
    this.error = '';
    try {
      // The server only returns profile-less users when no company filter is
      // sent, so ask for everyone and split here.
      this.allDrivers = await this.svc.listDrivers();
      this.render();
    } catch (err: any) {
      this.error = err?.error?.message || err?.message || 'Could not load drivers';
    }
    this.loading = false;
  }

  private errorText(err: any, fallback: string) {
    return err?.error?.message || err?.message || fallback;
  }

  // Role + panel-feature inputs shared by the add and edit popups.
  private roleAndFeatureFields(role: string, features: string[]) {
    return [
      {
        labelName: 'Role',
        name: 'role',
        required: true,
        value: role,
        selectOptions: this.roles.map(r => ({ value: r.value, label: `${r.label} — ${r.hint}` }))
      },
      ...this.featureOptions.map(f => ({
        labelName: `Panel access: ${f.label}`,
        name: `feature_${f.value}`,
        type: 'checkbox',
        value: features.includes(f.value),
        hint: f.hint
      }))
    ];
  }

  private pickedFeatures(values: any): string[] {
    return this.featureOptions.filter(f => values[`feature_${f.value}`] === true).map(f => f.value);
  }

  // New driver, always in the company the dashboard is showing. Adding an email
  // that already is a driver adds this company to theirs.
  openAdd() {
    const companyId = this.config.getCompanyId();
    if (!companyId) {
      this.notifier.showNotification('warning', 'Pick a company in the dashboard first');
      return;
    }
    const ref = this.dialog.open(CommonPopup, {
      disableClose: true,
      width: '520px',
      data: {
        formData: {
          title: `New driver for ${this.currentCompanyName()}`,
          inputFeilds: [
            { labelName: 'Email', name: 'email', type: 'text', required: true, value: '',
              hint: 'An existing admin login is linked; it keeps its own name.' },
            { labelName: 'Name', name: 'name', type: 'text', required: false, value: '' },
            { labelName: 'Phone', name: 'phone', type: 'text', required: false, value: '' },
            { labelName: 'Employer', name: 'employer', type: 'text', required: false, value: '' },
            ...this.roleAndFeatureFields('driver', [])
          ],
          buttons: [{ name: 'ADD', type: 'submit', color: 'primary', validate: true }],
          onSubmit: async (v: any) => {
            try {
              const email = (v.email || '').trim().toLowerCase();
              const known = this.allDrivers.find(d => !d.profileMissing && (d.email || '').toLowerCase() === email);
              if (known) {
                await this.svc.updateDriver(known.uid, {
                  companyIds: Array.from(new Set([...(known.companyIds || []), companyId]))
                });
                return { success: true };
              }
              const res: any = await this.svc.createDriver({
                email, name: (v.name || '').trim(), phone: (v.phone || '').trim(),
                employer: (v.employer || '').trim(), role: v.role,
                companyIds: [companyId], features: this.pickedFeatures(v)
              });
              if (res && res.invited === false) {
                this.notifier.showNotification('warning',
                  'Driver created, but the invite email could not be sent. They can use "forgot password".');
              }
              return { success: true };
            } catch (err: any) {
              return { success: false, message: this.errorText(err, 'Could not add the driver') };
            }
          }
        }
      }
    });
    ref.afterClosed().subscribe(res => { if (res) { this.notifier.showNotification('success', 'Driver added'); this.load(); } });
  }

  // Details popup. `unassigned` rows get the selected company when saved.
  openDetails(row: DriverRow, unassigned: boolean) {
    const companyId = this.config.getCompanyId();
    if (unassigned && !companyId) {
      this.notifier.showNotification('warning', 'Pick a company in the dashboard first');
      return;
    }
    const ref = this.dialog.open(CommonPopup, {
      disableClose: true,
      width: '520px',
      data: {
        formData: {
          title: unassigned
            ? `Driver details — adds ${row.email} to ${this.currentCompanyName()}`
            : `Edit driver — ${row.email}`,
          inputFeilds: [
            { labelName: 'Name', name: 'name', type: 'text', value: row.name, disabled: true,
              hint: 'The name comes from the user account.' },
            { labelName: 'Phone', name: 'phone', type: 'text', required: false, value: row.phone || '' },
            { labelName: 'Employer', name: 'employer', type: 'text', required: false, value: row.employer || '' },
            ...this.roleAndFeatureFields(row.role || 'driver', row.features || [])
          ],
          buttons: [{ name: 'SAVE', type: 'submit', color: 'primary', validate: true }],
          onSubmit: async (v: any) => {
            try {
              await this.svc.updateDriver(row.uid, {
                name: row.name,
                phone: (v.phone || '').trim(),
                employer: (v.employer || '').trim(),
                role: v.role,
                features: this.pickedFeatures(v),
                // Company membership of an existing driver is changed from the
                // list; only an unassigned user gets the selected company here.
                ...(unassigned ? { companyIds: [companyId] } : {})
              });
              return { success: true };
            } catch (err: any) {
              return { success: false, message: this.errorText(err, 'Could not save the driver') };
            }
          }
        }
      }
    });
    ref.afterClosed().subscribe(res => {
      if (res) {
        this.notifier.showNotification('success', unassigned ? 'Driver added to the company' : 'Driver updated');
        this.load();
      }
    });
  }

  // One click: the unassigned driver joins the selected company.
  async assign(row: DriverRow) {
    const companyId = this.config.getCompanyId();
    if (!companyId) {
      this.notifier.showNotification('warning', 'Pick a company in the dashboard first');
      return;
    }
    try {
      await this.svc.updateDriver(row.uid, {
        name: row.name,
        companyIds: Array.from(new Set([...(row.companyIds || []), companyId]))
      });
      this.notifier.showNotification('success', `${row.name || row.email} added to ${this.currentCompanyName()}`);
      await this.load();
    } catch (err: any) {
      this.notifier.showNotification('error', this.errorText(err, 'Could not add the driver to the company'));
    }
  }

  private confirm(title: string, message: string): Promise<boolean> {
    const ref = this.dialog.open(DialogDelete, {
      width: '550px',
      disableClose: true,
      data: { formData: { title, message, buttons: ['Cancel', 'Yes'] } }
    });
    return ref.afterClosed().toPromise().then(r => !!r);
  }

  // Only possible when the driver works for another company too — the server
  // refuses a driver with no company at all (deactivate them instead).
  async removeFrom(row: DriverRow) {
    const companyId = this.config.getCompanyId();
    const rest = (row.companyIds || []).filter(id => id !== companyId);
    if (!rest.length) {
      this.notifier.showNotification('info',
        'This is the only company of the driver. Deactivate the driver instead.');
      return;
    }
    if (!await this.confirm('REMOVE FROM COMPANY', `Remove ${row.name || row.email} from ${this.currentCompanyName()}?`)) return;
    try {
      await this.svc.updateDriver(row.uid, { companyIds: rest });
      this.notifier.showNotification('success', 'Driver removed from the company');
      await this.load();
    } catch (err: any) {
      this.notifier.showNotification('error', this.errorText(err, 'Could not remove the driver'));
    }
  }

  async setStatus(row: DriverRow) {
    const status = !row.status;
    if (!await this.confirm(status ? 'REACTIVATE DRIVER' : 'DEACTIVATE DRIVER',
      `${status ? 'Reactivate' : 'Deactivate'} ${row.name || row.email}?`)) return;
    try {
      await this.svc.updateDriver(row.uid, { status });
      // Deactivating revokes refresh tokens server-side, so a driver mid-shift
      // loses access as soon as their current ID token expires.
      this.notifier.showNotification('success', status ? 'Driver reactivated' : 'Driver deactivated');
      await this.load();
    } catch (err: any) {
      this.notifier.showNotification('error', this.errorText(err, 'Could not change the status'));
    }
  }

  async loadSupport() {
    try {
      const s = await this.svc.getDriverSettings();
      this.support = {
        supportPhone: s.supportPhone || '',
        roadAssistPhone: s.roadAssistPhone || '',
        extraContacts: (s.extraContacts || []).map(c => ({ ...c }))
      };
      this.supportLoaded = true;
    } catch (err: any) {
      this.notifier.showNotification('error', this.errorText(err, 'Could not load the settings'));
    }
  }

  addContact() {
    if (this.support.extraContacts.length < 6) this.support.extraContacts.push({ label: '', phone: '' });
  }

  removeContact(i: number) {
    this.support.extraContacts.splice(i, 1);
  }

  async saveSupport() {
    this.supportSaving = true;
    try {
      await this.svc.saveDriverSettings(this.support);
      // Drivers get the new numbers with their next route refresh.
      this.notifier.showNotification('success', 'Settings saved');
    } catch (err: any) {
      this.notifier.showNotification('error', this.errorText(err, 'Could not save the settings'));
    }
    this.supportSaving = false;
  }

  async resendInvite(row: DriverRow) {
    try {
      await this.svc.resendInvite(row.uid);
      this.notifier.showNotification('success', 'Invite sent');
    } catch (err: any) {
      this.notifier.showNotification('error', this.errorText(err, 'Could not send the invite'));
    }
  }
}
